import MDEditor from '@uiw/react-md-editor';
import { Formik } from 'formik';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  InlineLoading,
  InlineNotification,
  Select,
  SelectItem,
  Stack,
  TextInputSkeleton,
} from '@carbon/react';
import { FormikTextInput } from 'src/components/Form/FormikInputs';
import { useClearSavedState } from 'src/hooks/useClearSavedState';
import { useBranches } from 'src/hooks/useRestaurantBranches';
import { useStorefront, useUpdateStorefrontMutation } from 'src/hooks/useStorefront';

type StorefrontContentForm = {
  aboutMarkdown: string;
  contactPhone: string;
  contactEmail: string;
  contactAddress: string;
  defaultBranchId: string;
};

/**
 * Every contact override is optional on the update DTO, so a cleared field sends an empty string
 * rather than being dropped — that is how the owner erases one. `defaultBranchId` is the
 * exception, and it sends `null`, not `undefined`.
 *
 * `undefined` was wrong in a way that only showed up on the second visit: the key is dropped from
 * the JSON body, and the server's update loop writes a field only `if (dto[field] !== undefined)`.
 * So the empty option could set nothing and clear nothing — once a default branch was saved, it
 * was permanent. `null` is what the API reads as "use the oldest branch": `@IsOptional()` skips
 * the uuid validation for it, and the loop writes it through.
 *
 * The cast is for the generated client only — the API declares `nullable: true` here and
 * swagger-typescript drops it when it writes `types.ts`.
 */
function toContentPayload(values: StorefrontContentForm) {
  return {
    aboutMarkdown: values.aboutMarkdown,
    contactPhone: values.contactPhone,
    contactEmail: values.contactEmail,
    contactAddress: values.contactAddress,
    defaultBranchId: (values.defaultBranchId || null) as unknown as string | undefined,
  };
}

const StorefrontContent = React.memo(() => {
  const { t } = useTranslation();
  const { data: storefront, isLoading } = useStorefront();
  const { data: branches } = useBranches();
  const {
    mutate: updateStorefront,
    isLoading: saving,
    isSuccess: saved,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateStorefrontMutation();

  useClearSavedState(saved, resetUpdate);

  const handleSubmit = useCallback(
    (values: StorefrontContentForm) => {
      updateStorefront(toContentPayload(values));
    },
    [updateStorefront]
  );

  return (
    <Stack gap={6}>
      {isLoading && <TextInputSkeleton />}
      {!isLoading && storefront && (
        <Formik<StorefrontContentForm>
          initialValues={{
            aboutMarkdown: storefront.aboutMarkdown ?? '',
            contactPhone: storefront.contactPhone ?? '',
            contactEmail: storefront.contactEmail ?? '',
            contactAddress: storefront.contactAddress ?? '',
            defaultBranchId: storefront.defaultBranchId ?? '',
          }}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ handleSubmit: submitForm, values, setFieldValue }) => (
            <Form onSubmit={submitForm}>
              <Stack gap={6}>
                {/* The editor renders its own toolbar, and a dark wrapper breaks the Carbon theme
                 * around it — the same wrapper MenuItemForm uses for its details field. Always light:
                 * the editor has no Carbon theme of its own.
                 *
                 * A plain `cds--label`, not FormLabel: FormLabel adds `cds--label--no-margin`, which
                 * sat the label flush on the editor where Carbon's own fields leave 0.5rem. It is
                 * also a real <label> for the textarea now, which FormLabel's was not. */}
                <div data-color-mode="light">
                  <label className="cds--label" htmlFor="storefront-about">
                    {t('storefront.content.about')}
                  </label>
                  <MDEditor
                    preview="edit"
                    height={200}
                    textareaProps={{ id: 'storefront-about' }}
                    value={values.aboutMarkdown}
                    onChange={(value) => setFieldValue('aboutMarkdown', value ?? '')}
                  />
                  <p className="cds--form__helper-text">{t('storefront.content.aboutHint')}</p>
                </div>

                <h4 className="storefront-content__section">{t('storefront.content.contact')}</h4>
                <FormikTextInput name={'contactPhone'} label={t('storefront.content.contactPhone')} />
                <FormikTextInput name={'contactEmail'} label={t('storefront.content.contactEmail')} />
                <FormikTextInput name={'contactAddress'} label={t('storefront.content.contactAddress')} />

                <InlineNotification
                  kind="info"
                  lowContrast
                  title={t('storefront.content.contactEmail')}
                  subtitle={t('storefront.content.notificationHint')}
                />

                <Select
                  id="defaultBranchId"
                  labelText={t('storefront.content.defaultBranch')}
                  helperText={t('storefront.content.defaultBranchHint')}
                  value={values.defaultBranchId}
                  onChange={(event) => setFieldValue('defaultBranchId', event.target.value)}
                >
                  {/* The empty option is not a placeholder — the API reads it as "the oldest
                   * branch", and the helper text above says so. */}
                  <SelectItem value="" text={t('common.selectABranch')} />
                  {(branches?.data ?? []).map((branch) => (
                    <SelectItem key={branch.id} value={branch.id} text={branch.name} />
                  ))}
                </Select>

                <div className="storefront-content__actions">
                  {saving || saved ? (
                    <InlineLoading
                      description={saved ? t('common.saved') : `${t('common.saving')}...`}
                      status={saved ? 'finished' : 'active'}
                    />
                  ) : (
                    <Button type="submit">{t('storefront.actions.save')}</Button>
                  )}
                </div>

                {updateError && <InlineNotification kind="error" subtitle={t('messages.somethingWentWrong')} />}
              </Stack>
            </Form>
          )}
        </Formik>
      )}
    </Stack>
  );
});

export default StorefrontContent;
